import { useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import { UseRefinementListProps, useRefinementList } from 'react-instantsearch';

import IconWrapper from '@components/ui/Icons';
import { colours } from './utils';

import useTabVisibility from '@hooks/useTabVisibility';

import styles from './ColourRefinement.module.scss';

const refinementListClassNames = {
  root: styles.refinementListRoot,
  searchBox: styles.refinementListSearchBox,
  noRefinementRoot: styles.noRefinementRoot,
  list: styles.refinementListList,
  item: styles.refinementListItem,
  label: styles.refinementListLabel,
  labelText: styles.refinementListLabelText,
  count: styles.refinementListCount,
  checkbox: styles.refinementListCheckbox,
  showMore: styles.refinementListshowMore,
};

const ColourRefinement = (
  props: UseRefinementListProps & { locale?: string }
) => {
  const { items, refine } = useRefinementList({
    ...props,
    sortBy: ['isRefined'],
    limit: 100,
    operator: 'or',
  });

  const { t } = useTranslation('searchResults');
  const clearButtonRef = useRef<HTMLButtonElement>(null);

  const refinedItems = items.filter((item) => item.isRefined);
  const isClearButtonDisplayed = refinedItems.length > 1;

  const formatNumber = (count: number) =>
    new Intl.NumberFormat(props.locale ?? 'en').format(count);

  useTabVisibility(clearButtonRef);

  const [isDescriptive, setIsDescriptive] = useState(false);

  return (
    <div className={refinementListClassNames.root}>
      <label className={styles.descriptionLabel}>
        <input
          type="checkbox"
          checked={isDescriptive}
          onChange={(e) => {
            e.preventDefault();
            setIsDescriptive((state) => !state);
          }}
          className={cn(refinementListClassNames.checkbox)}
        />
        {t('search.facet.colour.toggleColourInfo')}
      </label>
      <ul
        className={cn(refinementListClassNames.list, {
          [styles.swatchListCondensed]: !isDescriptive,
        })}
      >
        {items.map((item) => (
          <li key={item.label} className={refinementListClassNames.item}>
            <label
              className={refinementListClassNames.label}
              aria-label={item.label}
            >
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={(e) => {
                  e.preventDefault();
                  refine(item.value);
                }}
                className={cn(refinementListClassNames.checkbox, {
                  [styles.checkboxCondensed]: !isDescriptive,
                })}
              />
              <span
                style={{
                  background:
                    colours[item.value]?.colour ?? 'var(--color-greige)',
                }}
                className={cn(styles.colourSwatch, {
                  [styles.isSelected]: item.isRefined,
                  [styles.condensedSwatch]: !isDescriptive,
                })}
                title={
                  props.locale === 'fr'
                    ? colours[item.value]?.frenchSwatchName
                    : item.label
                }
              >
                {!isDescriptive && item.isRefined && (
                  <IconWrapper
                    icon="checkmark"
                    width={16}
                    fill={
                      colours[item.value]?.isWhiteReadable ? 'white' : 'black'
                    }
                  />
                )}
              </span>{' '}
              {isDescriptive && (
                <>
                  <span className={refinementListClassNames.labelText}>
                    {props.locale === 'fr'
                      ? colours[item.value]?.frenchSwatchName
                      : item.label}
                  </span>
                  <span
                    className={cn(
                      refinementListClassNames.count,
                      refinementListClassNames.labelText
                    )}
                  >
                    {' '}
                    ({formatNumber(item.count)})
                  </span>
                </>
              )}
            </label>
          </li>
        ))}
        <li
          className={cn(refinementListClassNames.item, styles.clearFilterItem, {
            [styles.clearFilterDisplayed]: isClearButtonDisplayed,
          })}
        >
          <button
            onClick={() => refinedItems.forEach((item) => refine(item.value))}
            className={cn(
              refinementListClassNames.label,
              styles.clearFilterButton
            )}
            ref={clearButtonRef}
          >
            {t('search.facets.productRating.clear')}
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ColourRefinement;
